import React, { useState, useEffect } from "react";
import "../assets/styles/pages.css";
import "../assets/styles/checkout.css";
import Button from "../components/Button";
import FormInput from "../components/FormInput";
import FormPhoneInput from "../components/FormPhoneInput";
import UserService from "../services/UserService";
import { ACService } from "../services/ACService";
import { isValidPhoneNumber } from "react-phone-number-input";

import "../assets/styles/formLead.css";

const FormLead = ({
  title,
  ctaLabel,
  colorScheme = "light",
  acList = 10,
  acTag = "",
  acTags = [],
  acMeta = {},
  successMessage = "Inscrição confirmada. Você receberá o link e o lembrete do evento no seu WhatsApp.",
  whatsappNumber = "551151072423",
  whatsappMessage = "Quero%20confirmar%20minha%20inscri%C3%A7%C3%A3o",
  onSuccess,
  // Variante opcional (usada na LP Prosperidade): labels acima dos campos,
  // eyebrow/subtítulo e disclaimer customizáveis. O comportamento de envio
  // (ActiveCampaign + cadastro) é o mesmo.
  variant = "default",
  eyebrow = "",
  subtitle = "",
  disclaimer = "Gratuito. Sem spam. Você receberá apenas o lembrete do evento e o link de acesso.",
}) => {
  const stacked = variant === "stacked";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [redirectCountdown, setRedirectCountdown] = useState(0);
  const [isSuccess, setIsSuccess] = useState(false);
  const [hasRedirected, setHasRedirected] = useState(false);

  // Hook para gerenciar o redirect após sucesso
  useEffect(() => {
    let countdownInterval;

    if (isSuccess && !hasRedirected && redirectCountdown === 0) {
      if (onSuccess) {
        onSuccess();
        setHasRedirected(true);
        return;
      }
      console.log("[FormLead] Success detected, starting WhatsApp redirect countdown...");
      setRedirectCountdown(5);
      
      countdownInterval = setInterval(() => {
        setRedirectCountdown(prev => {
          if (prev <= 1) {
            // Limpa o interval antes do redirect
            clearInterval(countdownInterval);
            // Redirect para o WhatsApp com mensagem usando location.href para evitar popup blocker
            const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;
            console.log("[FormLead] Redirecting to WhatsApp:", whatsappUrl);
            setHasRedirected(true); // Marca como já redirecionado
            
            // Delay pequeno para garantir que o state foi atualizado
            setTimeout(() => {
              window.location.href = whatsappUrl;
            }, 100);
            
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownInterval) {
        console.log("[FormLead] Cleaning up countdown interval");
        clearInterval(countdownInterval);
      }
    };
  }, [isSuccess, hasRedirected, redirectCountdown]);

  const isDisabled =
    loading ||
    !name.trim() ||
    !email.trim() ||
    !phone.trim() ||
    !isValidPhoneNumber(phone) ||
    !email.includes("@");

  const handleRegister = async (e) => {
    e.preventDefault();
    if (isDisabled) return;
    setLoading(true);
    setFeedback("");
    setIsSuccess(false); // Reset success state
    setRedirectCountdown(0); // Reset countdown
    setHasRedirected(false); // Reset redirect flag

    const cleanName = name.trim();
    const cleanEmail = email.trim();
    const cleanPhone = phone.trim();
    const normalizedTags = Array.from(
      new Set(
        [acTag, ...(Array.isArray(acTags) ? acTags : [])]
          .map((item) => String(item || "").trim())
          .filter(Boolean)
      )
    );
    const acPrimaryTag =
      normalizedTags.length > 1 ? normalizedTags.join(" | ") : normalizedTags[0] || "";

    try {
      const acResponse = await ACService.saveLead(cleanName, cleanEmail, cleanPhone, acList, acPrimaryTag, {
        tags: normalizedTags,
        source: acMeta?.source || "",
        variant: acMeta?.variant || "",
        page: acMeta?.page || "",
        route: acMeta?.route || "",
        utm_source: acMeta?.utms?.utm_source || "",
        utm_medium: acMeta?.utms?.utm_medium || "",
        utm_campaign: acMeta?.utms?.utm_campaign || "",
        utm_content: acMeta?.utms?.utm_content || "",
        utm_term: acMeta?.utms?.utm_term || "",
        vk_source: acMeta?.utms?.vk_source || "",
        vk_ad_id: acMeta?.utms?.vk_ad_id || "",
      });

      console.log("[FormLead] ACService.saveLead response:", acResponse);

      try {
        const res = await UserService.register({
          usr_email: cleanEmail,
          usr_name: cleanName,
          usr_phone: cleanPhone,
        });

        if (res?.status !== 1 && !/já.*cadastr/i.test(res?.message || "")) {
          console.warn("[FormLead] cadastro não concluído:", res?.message || "sem mensagem");
        }
      } catch (registerError) {
        console.warn("[FormLead] erro ao registrar usuário:", registerError?.message || registerError);
      }

      // Marca como sucesso e exibe mensagem
      setIsSuccess(true);
      setFeedback(successMessage);
      if (onSuccess) onSuccess();
      console.log("[FormLead] Success! Starting WhatsApp redirect countdown...");
      setName("");
      setEmail("");
      setPhone("");
    } catch (error) {
      console.error("[FormLead] erro ao enviar lead:", error);
      setIsSuccess(false);
      setFeedback(error.message || "Erro ao enviar seus dados.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`FormLead${stacked ? " FormLead--stacked" : ""}`}>
      <div className={`container-fluid ${colorScheme === "light" ? "text-dark" : "text-light"}`}>
        <div className="row justify-content-center">
          <div className="col-12 px-0">
            {eyebrow && <p className="FormLead-eyebrow">{eyebrow}</p>}
            <h2 className="mb-2 text-center">
              <strong>
                {title}
              </strong>
            </h2>
            
            {subtitle ? (
              <p className="FormLead-subtitle">{subtitle}</p>
            ) : (
              <p className="inscrevase mb-4 text-center">
                  Inscreva-se agora e receba o link de acesso e o lembrete no seu WhatsApp.
              </p>
            )}

            <form onSubmit={handleRegister}>
              {stacked && <label htmlFor="name" className="form-label">Nome</label>}
              <FormInput
                required
                float={!stacked}
                type="text"
                name="name"
                label="Nome completo"
                placeholder={stacked ? "Seu nome completo" : "Informe seu nome completo"}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              {stacked && <label htmlFor="email" className="form-label">E-mail</label>}
              <FormInput
                required
                float={!stacked}
                type="email"
                name="email"
                label="E-mail"
                placeholder={stacked ? "Seu melhor e-mail" : "Informe seu melhor e-mail"}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <FormPhoneInput
                required
                name="phone"
                label="WhatsApp"
                placeholder="Digite seu telefone"
                float={!stacked}
                value={phone}
                onChange={setPhone}
                invalid={!!phone && !isValidPhoneNumber(phone)}
              />
              <div className="mt-3">
                <Button
                  type="submit"
                  classes="w-100"
                  disabled={isDisabled}
                  label={
                    loading ? "Enviando seus dados..." : ctaLabel ?? "Quero minha vaga"
                  }
                />
              </div>

              {feedback && (
                <div
                  className={`alert p-2 my-3 small ${
                    feedback.toLowerCase().includes("erro") || !isSuccess
                      ? "alert-danger"
                      : "alert-success"
                  }`}
                >
                  {feedback}
                  {/* {isSuccess && redirectCountdown > 0 && (
                    <div className="mt-2">
                      <small className="d-block">
                        Redirecionando para o WhatsApp em {redirectCountdown} segundos...
                      </small>
                      <button 
                        type="button"
                        className="btn btn-sm btn-outline-success mt-2"
                        onClick={() => {
                          const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;
                          window.location.href = whatsappUrl;
                        }}
                      >
                        Ir para WhatsApp agora
                      </button>
                    </div>
                  )} */}
                  {isSuccess && redirectCountdown === 0 && !hasRedirected && (
                    <div className="mt-2">
                      <small className="d-block text-muted">
                        Não foi redirecionado automaticamente?
                      </small>
                      <button 
                        type="button"
                        className="btn btn-sm btn-success mt-2"
                        onClick={() => {
                          const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;
                          window.location.href = whatsappUrl;
                        }}
                      >
                        Abrir WhatsApp manualmente
                      </button>
                    </div>
                  )}
                </div>
              )}
              <p className="mt-3 mx-auto text-center disclaimer" style={{maxWidth: '90%'}}>
                {disclaimer}
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormLead;
